import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ArrowRightIcon, ChevronRightIcon, PlayCircleIcon, ShieldIcon, BookOpenIcon, AwardIcon, BarChartIcon, UsersIcon, GlobeIcon, BuildingIcon, UserIcon, BriefcaseIcon, LightbulbIcon, MousePointerIcon, ClockIcon, SmartphoneIcon, BadgeCheckIcon, StarIcon, ArrowUpRightIcon } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
export const MagicHomepage = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-charcoal" data-id="element-839">
      <style jsx global data-id="element-840">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        html {
          scroll-behavior: smooth;
        }
        body {
          font-family: 'Inter', sans-serif;
        }
        .gradient-text {
          background: linear-gradient(135deg, #0b757a 0%, #1ddbdb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .gradient-border {
          position: relative;
          border-radius: 0.75rem;
          z-index: 0;
        }
        .gradient-border::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 0.75rem;
          padding: 1px;
          background: linear-gradient(135deg, #0b757a 0%, #1ddbdb 100%);
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          z-index: -1;
        }
        .parallax-bg {
          transform: translateZ(-10px) scale(2);
          z-index: -1;
        }
        .card-hover {
          transition: all 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-8px);
        }
        .grid-animate > * {
          opacity: 0;
          transform: translateY(20px);
          transition:
            opacity 0.5s ease,
            transform 0.5s ease;
        }
        .grid-animate.visible > * {
          opacity: 1;
          transform: translateY(0);
        }
        .grid-animate.visible > *:nth-child(1) {
          transition-delay: 0.1s;
        }
        .grid-animate.visible > *:nth-child(2) {
          transition-delay: 0.2s;
        }
        .grid-animate.visible > *:nth-child(3) {
          transition-delay: 0.3s;
        }
        .grid-animate.visible > *:nth-child(4) {
          transition-delay: 0.4s;
        }
        .grid-animate.visible > *:nth-child(5) {
          transition-delay: 0.5s;
        }
        .grid-animate.visible > *:nth-child(6) {
          transition-delay: 0.6s;
        }
        .grid-animate.visible > *:nth-child(7) {
          transition-delay: 0.7s;
        }
        .grid-animate.visible > *:nth-child(8) {
          transition-delay: 0.8s;
        }
      `}</style>
      <Header data-id="element-841" />
      <main className="flex-grow" data-id="element-842">
        <HeroSection data-id="element-843" />
        <TrustedBySection data-id="element-844" />
        <ValuePropositionSection data-id="element-845" />
        <FeaturesSection data-id="element-846" />
        <CourseCategoriesSection data-id="element-847" />
        <TestimonialsSection data-id="element-848" />
        <EnterpriseSection data-id="element-849" />
        <PricingSection data-id="element-850" />
        <CtaSection data-id="element-851" />
      </main>
      <Footer data-id="element-852" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-charcoal/90 to-charcoal/95" data-id="element-853">
      {/* Full width background image with overlay */}
      <div className="absolute inset-0 z-0" data-id="element-854">
        <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-855" />
        <div className="absolute inset-0 bg-charcoal/70" data-id="element-856"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 to-charcoal/80" data-id="element-857"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-858"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10 pt-20 pb-24 md:pt-32 md:pb-32" data-id="element-859">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto" data-id="element-860">
          <div className={`transition-all duration-700 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-861">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6" data-id="element-862">
              <BadgeCheckIcon className="h-4 w-4 text-aqua mr-2" data-id="element-863" />
              <span className="text-white/90 text-sm font-medium" data-id="element-864">
                Trusted by 10,000+ companies worldwide
              </span>
            </div>
          </div>
          <h1 className={`text-5xl md:text-6xl xl:text-7xl font-bold tracking-tight leading-none mb-6 text-white transition-all duration-700 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-865">
            <span className="gradient-text" data-id="element-866">Elevate</span> Your Workforce.
            <br data-id="element-867" />
            <span className="gradient-text" data-id="element-868">Ensure</span> Compliance.
          </h1>
          <p className={`text-xl md:text-2xl text-white/80 mb-10 max-w-3xl transition-all duration-700 delay-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-869">
            World-class compliance training and professional development built
            for modern organizations across Ireland, the UK, and Europe.
          </p>
          <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-900 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-870">
            <a href="/explore" className="group relative inline-flex items-center justify-center px-8 py-4 font-medium text-white bg-teal rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-871">
              <span className="absolute inset-0 w-full h-full bg-gradient-to-br from-teal to-aqua transition-all duration-300 transform group-hover:scale-105" data-id="element-872"></span>
              <span className="relative flex items-center" data-id="element-873">
                Explore Courses
                <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-874" />
              </span>
            </a>
            <a href="/demo" className="inline-flex items-center justify-center px-8 py-4 font-medium text-white bg-transparent border-2 border-white/30 rounded-lg hover:bg-white/10 transition-all duration-300" data-id="element-875">
              <PlayCircleIcon className="mr-2 h-5 w-5" data-id="element-876" />
              Watch Demo
            </a>
          </div>
          <div className={`mt-16 grid grid-cols-3 gap-8 transition-all duration-700 delay-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-877">
            <div className="text-center" data-id="element-878">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-879">
                125+
              </div>
              <div className="text-white/60" data-id="element-880">Courses</div>
            </div>
            <div className="text-center" data-id="element-881">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-882">
                97%
              </div>
              <div className="text-white/60" data-id="element-883">Completion Rate</div>
            </div>
            <div className="text-center" data-id="element-884">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-885">
                100%
              </div>
              <div className="text-white/60" data-id="element-886">Compliance</div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrustedBySection = () => {
  return <section className="py-16 bg-white border-b border-gray-100" data-id="element-887">
      <div className="container mx-auto px-4 md:px-6" data-id="element-888">
        <div className="text-center mb-10" data-id="element-889">
          <p className="text-lg text-charcoal/70 font-medium" data-id="element-890">
            Trusted by leading organizations across industries
          </p>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-8" data-id="element-891">
          <div className="flex flex-col items-center" data-id="element-892">
            <div className="text-2xl font-bold text-charcoal/40" data-id="element-893">
              CPD Certified
            </div>
          </div>
          <div className="flex flex-col items-center" data-id="element-894">
            <div className="text-2xl font-bold text-charcoal/40" data-id="element-895">
              ROSPA Member
            </div>
          </div>
          <div className="flex flex-col items-center" data-id="element-896">
            <div className="text-2xl font-bold text-charcoal/40" data-id="element-897">
              Irish Trade Federation
            </div>
          </div>
          <div className="flex flex-col items-center" data-id="element-898">
            <div className="text-2xl font-bold text-charcoal/40" data-id="element-899">ISO 27001</div>
          </div>
          <div className="flex flex-col items-center" data-id="element-900">
            <div className="text-2xl font-bold text-charcoal/40" data-id="element-901">
              EU Compliant
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const ValuePropositionSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-902">
      <div className="container mx-auto px-4 md:px-6" data-id="element-903">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-904">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-905">
            <AwardIcon className="h-6 w-6 text-teal" data-id="element-906" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal" data-id="element-907">
            One Platform. <span className="gradient-text" data-id="element-908">Every Solution.</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-909">
            Kivarro's integrated training ecosystem serves every need—from
            individual learners to global enterprises.
          </p>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto grid-animate ${isVisible ? 'visible' : ''}`} data-id="element-910">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 card-hover" data-id="element-911">
            <div className="w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-912">
              <UserIcon className="w-8 h-8 text-teal" data-id="element-913" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-charcoal" data-id="element-914">
              For Individuals
            </h3>
            <p className="text-charcoal/70 mb-6" data-id="element-915">
              Enhance your career with on-demand professional training. Get
              certified instantly and showcase your expertise.
            </p>
            <a href="/individuals" className="inline-flex items-center font-medium text-teal hover:text-aqua transition-colors" data-id="element-916">
              Learn more
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-917" />
            </a>
          </div>
          <div className="bg-gradient-to-br from-teal to-teal/90 rounded-xl p-8 shadow-lg card-hover" data-id="element-918">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-6" data-id="element-919">
              <UsersIcon className="w-8 h-8 text-white" data-id="element-920" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-921">For Teams</h3>
            <p className="text-white/90 mb-6" data-id="element-922">
              Streamline training management with powerful tools to assign,
              track, and report on team progress.
            </p>
            <a href="/teams" className="inline-flex items-center font-medium text-white hover:text-white/80 transition-colors" data-id="element-923">
              Learn more
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-924" />
            </a>
          </div>
          <div className="bg-charcoal rounded-xl p-8 shadow-lg card-hover" data-id="element-925">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-6" data-id="element-926">
              <BuildingIcon className="w-8 h-8 text-white" data-id="element-927" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-928">
              For Enterprises
            </h3>
            <p className="text-white/90 mb-6" data-id="element-929">
              Deploy custom-branded academies with advanced analytics, SSO
              integration, and dedicated support.
            </p>
            <a href="/enterprise" className="inline-flex items-center font-medium text-white hover:text-white/80 transition-colors" data-id="element-930">
              Learn more
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-931" />
            </a>
          </div>
        </div>
      </div>
    </section>;
};
const FeaturesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-gray-50" data-id="element-932">
      <div className="container mx-auto px-4 md:px-6" data-id="element-933">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-934">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-935">
            <LightbulbIcon className="h-6 w-6 text-teal" data-id="element-936" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal" data-id="element-937">
            Premium Benefits.{' '}
            <span className="gradient-text" data-id="element-938">Unmatched Value.</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-939">
            Discover why thousands of organizations choose Kivarro for their
            training needs.
          </p>
        </div>
        <div className="max-w-6xl mx-auto" data-id="element-940">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 grid-animate ${isVisible ? 'visible' : ''}`} data-id="element-941">
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-942">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-943">
                <BadgeCheckIcon className="w-6 h-6 text-teal" data-id="element-944" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-945">
                Instant Certification
              </h3>
              <p className="text-charcoal/70" data-id="element-946">
                Receive your certificate immediately upon course
                completion—downloadable and verifiable.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-947">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-948">
                <ClockIcon className="w-6 h-6 text-teal" data-id="element-949" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-950">
                24/7 Accessibility
              </h3>
              <p className="text-charcoal/70" data-id="element-951">
                Train anytime, anywhere with unlimited access across all your
                devices.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-952">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-953">
                <SmartphoneIcon className="w-6 h-6 text-teal" data-id="element-954" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-955">
                Mobile Learning
              </h3>
              <p className="text-charcoal/70" data-id="element-956">
                Download courses to our mobile app for offline learning on the
                go.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-957">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-958">
                <BarChartIcon className="w-6 h-6 text-teal" data-id="element-959" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-960">
                Advanced Analytics
              </h3>
              <p className="text-charcoal/70" data-id="element-961">
                Track progress, completions, and compliance status with detailed
                reporting tools.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-962">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-963">
                <GlobeIcon className="w-6 h-6 text-teal" data-id="element-964" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-965">
                Region-Specific Compliance
              </h3>
              <p className="text-charcoal/70" data-id="element-966">
                Courses tailored to meet Irish, UK, and EU regulatory
                requirements and standards.
              </p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full card-hover" data-id="element-967">
              <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center mb-6" data-id="element-968">
                <MousePointerIcon className="w-6 h-6 text-teal" data-id="element-969" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-charcoal" data-id="element-970">
                Interactive Learning
              </h3>
              <p className="text-charcoal/70" data-id="element-971">
                Engaging content with interactive elements designed for maximum
                knowledge retention.
              </p>
            </div>
          </div>
          <div className="mt-16 bg-gradient-to-r from-teal to-aqua rounded-xl overflow-hidden shadow-xl" data-id="element-972">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center" data-id="element-973">
              <div className="p-10 md:p-12" data-id="element-974">
                <div className="inline-block px-4 py-1 bg-white/20 rounded-full text-white text-sm font-medium mb-6" data-id="element-975">
                  Kivarro Guarantee
                </div>
                <h3 className="text-3xl font-bold text-white mb-4" data-id="element-976">
                  100% Acceptance Rate
                </h3>
                <p className="text-white/90 text-lg mb-6" data-id="element-977">
                  Our certificates are recognized by regulatory bodies and
                  auditors across Ireland, the UK, and Europe. If your
                  certificate is ever refused, we'll refund your purchase.
                </p>
                <a href="/guarantee" className="inline-flex items-center px-6 py-3 bg-white text-teal font-medium rounded-lg hover:bg-gray-100 transition-colors shadow-md" data-id="element-978">
                  Learn About Our Guarantee
                </a>
              </div>
              <div className="relative h-full min-h-[300px] md:min-h-[400px] bg-teal/80" data-id="element-979">
                <img src="https://images.unsplash.com/photo-1579444741963-5ae219cfe27c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Certification" className="w-full h-full object-cover mix-blend-overlay" data-id="element-980" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const CourseCategoriesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const categories = [{
    icon: <ShieldIcon className="w-6 h-6 text-white" data-id="element-981" />,
    title: 'Compliance Training',
    description: 'Essential regulatory training for all industries',
    count: '40+ Courses',
    color: 'from-blue-600 to-blue-700',
    link: '/compliance'
  }, {
    icon: <BookOpenIcon className="w-6 h-6 text-white" data-id="element-982" />,
    title: 'Food Safety',
    description: 'HACCP and food handling certifications',
    count: '15+ Courses',
    color: 'from-emerald-600 to-emerald-700',
    link: '/food-safety'
  }, {
    icon: <UsersIcon className="w-6 h-6 text-white" data-id="element-983" />,
    title: 'Leadership & Management',
    description: 'Team leadership and management skills',
    count: '25+ Courses',
    color: 'from-purple-600 to-purple-700',
    link: '/leadership'
  }, {
    icon: <BriefcaseIcon className="w-6 h-6 text-white" data-id="element-984" />,
    title: 'Professional Skills',
    description: 'Business and career advancement skills',
    count: '30+ Courses',
    color: 'from-teal to-teal/90',
    link: '/professional'
  }];
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-985">
      <div className="container mx-auto px-4 md:px-6" data-id="element-986">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-987">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-988">
            <BookOpenIcon className="h-6 w-6 text-teal" data-id="element-989" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal" data-id="element-990">
            World-Class{' '}
            <span className="gradient-text" data-id="element-991">Training Categories</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-992">
            Explore our comprehensive range of courses designed to meet your
            organization's needs
          </p>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto grid-animate ${isVisible ? 'visible' : ''}`} data-id="element-993">
          {categories.map((category, index) => <div key={index} className="group rounded-xl overflow-hidden shadow-lg card-hover" data-id="element-994">
              <div className={`bg-gradient-to-br ${category.color} p-8 h-full flex flex-col`} data-id="element-995">
                <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20" data-id="element-996">
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-997">
                  {category.title}
                </h3>
                <p className="text-white/80 mb-6 flex-grow" data-id="element-998">
                  {category.description}
                </p>
                <div className="flex items-center justify-between mt-auto" data-id="element-999">
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm" data-id="element-1000">
                    {category.count}
                  </span>
                  <a href={category.link} className="inline-flex items-center text-white font-medium group-hover:translate-x-1 transition-transform" data-id="element-1001">
                    Explore
                    <ChevronRightIcon className="h-5 w-5 ml-1" data-id="element-1002" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
        <div className="mt-16 text-center" data-id="element-1003">
          <a href="/courses" className="inline-flex items-center px-8 py-4 bg-charcoal text-white font-medium rounded-lg hover:bg-charcoal/90 transition-all duration-300 shadow-lg" data-id="element-1004">
            View All Courses
            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1005" />
          </a>
        </div>
      </div>
    </section>;
};
const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const testimonials = [{
    quote: 'The food safety training was incredibly comprehensive and engaging. I feel much more confident in my role now.',
    name: 'Sarah Johnson',
    title: 'Restaurant Manager',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    rating: 5
  }, {
    quote: 'The leadership course transformed how I manage my team. The practical strategies were easy to implement right away.',
    name: 'Michael Chen',
    title: 'Operations Director',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    rating: 5
  }, {
    quote: 'As a business owner, the GDPR training was invaluable. Clear, concise and exactly what we needed for compliance.',
    name: 'Emma Richards',
    title: 'CEO, TechStart',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=761&q=80',
    rating: 5
  }];
  return <section ref={sectionRef} className="py-24 bg-gray-50" data-id="element-1006">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1007">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1008">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-1009">
            <StarIcon className="h-6 w-6 text-teal" data-id="element-1010" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal" data-id="element-1011">
            What Our <span className="gradient-text" data-id="element-1012">Clients Say</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-1013">
            Join thousands of satisfied professionals who transformed their
            careers with Kivarro
          </p>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto grid-animate ${isVisible ? 'visible' : ''}`} data-id="element-1014">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 card-hover" data-id="element-1015">
              <div className="p-8" data-id="element-1016">
                <div className="flex mb-6" data-id="element-1017">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-amber-400' : 'text-gray-200'}`} data-id="element-1018" />)}
                </div>
                <blockquote className="text-charcoal/80 italic mb-6 text-lg" data-id="element-1019">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center" data-id="element-1020">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-1021" />
                  <div data-id="element-1022">
                    <p className="font-bold text-charcoal" data-id="element-1023">
                      {testimonial.name}
                    </p>
                    <p className="text-charcoal/60 text-sm" data-id="element-1024">
                      {testimonial.title}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8 md:p-10 max-w-4xl mx-auto border border-gray-100" data-id="element-1025">
          <div className="flex flex-col md:flex-row items-center justify-between" data-id="element-1026">
            <div className="mb-6 md:mb-0 text-center md:text-left" data-id="element-1027">
              <div className="text-4xl font-bold text-charcoal mb-2" data-id="element-1028">
                97% Completion Rate
              </div>
              <p className="text-xl text-charcoal/70" data-id="element-1029">
                Our training engages learners to completion
              </p>
            </div>
            <div className="flex space-x-6" data-id="element-1030">
              <div className="text-center" data-id="element-1031">
                <div className="text-3xl font-bold text-teal mb-1" data-id="element-1032">4.9/5</div>
                <p className="text-charcoal/60 text-sm" data-id="element-1033">Average Rating</p>
              </div>
              <div className="text-center" data-id="element-1034">
                <div className="text-3xl font-bold text-teal mb-1" data-id="element-1035">50k+</div>
                <p className="text-charcoal/60 text-sm" data-id="element-1036">
                  Certified Professionals
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const EnterpriseSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-charcoal text-white relative overflow-hidden" data-id="element-1037">
      <div className="absolute inset-0 overflow-hidden" data-id="element-1038">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-teal/10 blur-3xl" data-id="element-1039"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-aqua/10 blur-3xl" data-id="element-1040"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-1041"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1042">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto" data-id="element-1043">
          <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-1044">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-6" data-id="element-1045">
              <BuildingIcon className="h-7 w-7 text-aqua" data-id="element-1046" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-1047">
              Your Academy. <br data-id="element-1048" />
              <span className="gradient-text" data-id="element-1049">Your Brand.</span>
            </h2>
            <p className="text-white/80 text-xl mb-8" data-id="element-1050">
              Launch your own fully branded training academy with advanced
              features tailored to enterprise needs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-1051">
              <div className="flex items-start" data-id="element-1052">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1053">
                  <GlobeIcon className="w-5 h-5 text-aqua" data-id="element-1054" />
                </div>
                <div data-id="element-1055">
                  <h3 className="font-bold text-white mb-1" data-id="element-1056">Custom Branding</h3>
                  <p className="text-white/70 text-sm" data-id="element-1057">
                    Your logo, colors, and domain
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1058">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1059">
                  <BarChartIcon className="w-5 h-5 text-aqua" data-id="element-1060" />
                </div>
                <div data-id="element-1061">
                  <h3 className="font-bold text-white mb-1" data-id="element-1062">
                    Advanced Analytics
                  </h3>
                  <p className="text-white/70 text-sm" data-id="element-1063">
                    Detailed reporting and insights
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1064">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1065">
                  <UsersIcon className="w-5 h-5 text-aqua" data-id="element-1066" />
                </div>
                <div data-id="element-1067">
                  <h3 className="font-bold text-white mb-1" data-id="element-1068">Team Management</h3>
                  <p className="text-white/70 text-sm" data-id="element-1069">
                    Centralized user administration
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1070">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1071">
                  <AwardIcon className="w-5 h-5 text-aqua" data-id="element-1072" />
                </div>
                <div data-id="element-1073">
                  <h3 className="font-bold text-white mb-1" data-id="element-1074">
                    Dedicated Support
                  </h3>
                  <p className="text-white/70 text-sm" data-id="element-1075">
                    Priority service and account manager
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1076">
              <a href="/enterprise/demo" className="inline-flex items-center justify-center px-8 py-4 font-medium bg-white text-charcoal rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg" data-id="element-1077">
                Book a Demo
              </a>
              <a href="/enterprise" className="inline-flex items-center justify-center px-8 py-4 font-medium bg-transparent border-2 border-white/30 text-white rounded-lg hover:bg-white/10 transition-all duration-300" data-id="element-1078">
                Learn More
              </a>
            </div>
          </div>
          <div className={`relative transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-1079">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-1080">
              <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Academy Dashboard" className="w-full h-auto object-cover" data-id="element-1081" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent" data-id="element-1082"></div>
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg" data-id="element-1083">
                <div className="flex items-center" data-id="element-1084">
                  <div className="w-6 h-6 bg-teal rounded-md mr-3" data-id="element-1085"></div>
                  <span className="font-medium text-charcoal" data-id="element-1086">Your Brand</span>
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6" data-id="element-1087">
                <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg" data-id="element-1088">
                  <div className="flex items-center justify-between mb-3" data-id="element-1089">
                    <h4 className="font-semibold text-charcoal" data-id="element-1090">
                      Enterprise Dashboard
                    </h4>
                    <div className="flex space-x-1" data-id="element-1091">
                      <div className="w-2 h-2 rounded-full bg-teal animate-pulse" data-id="element-1092"></div>
                      <div className="w-2 h-2 rounded-full bg-teal/60 animate-pulse" style={{
                      animationDelay: '0.2s'
                    }} data-id="element-1093"></div>
                      <div className="w-2 h-2 rounded-full bg-teal/30 animate-pulse" style={{
                      animationDelay: '0.4s'
                    }} data-id="element-1094"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1095">
                    <div data-id="element-1096">
                      <div className="text-charcoal/60 mb-1" data-id="element-1097">Teams</div>
                      <div className="text-xl font-bold text-charcoal" data-id="element-1098">12</div>
                    </div>
                    <div data-id="element-1099">
                      <div className="text-charcoal/60 mb-1" data-id="element-1100">Users</div>
                      <div className="text-xl font-bold text-charcoal" data-id="element-1101">248</div>
                    </div>
                    <div data-id="element-1102">
                      <div className="text-charcoal/60 mb-1" data-id="element-1103">Compliance</div>
                      <div className="text-xl font-bold text-teal" data-id="element-1104">94%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 bg-white rounded-lg p-3 shadow-lg" data-id="element-1105">
              <div className="flex items-center space-x-2" data-id="element-1106">
                <div className="w-8 h-8 bg-teal/10 rounded-full flex items-center justify-center" data-id="element-1107">
                  <BadgeCheckIcon className="h-4 w-4 text-teal" data-id="element-1108" />
                </div>
                <span className="font-medium text-charcoal" data-id="element-1109">White Label</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const PricingSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-24 bg-white" data-id="element-1110">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1111">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1112">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-1113">
            <BadgeCheckIcon className="h-6 w-6 text-teal" data-id="element-1114" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal" data-id="element-1115">
            Simple, <span className="gradient-text" data-id="element-1116">Transparent Pricing</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-1117">
            Flexible options for individuals, teams, and enterprises
          </p>
        </div>
        <div className="max-w-6xl mx-auto" data-id="element-1118">
          <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 grid-animate ${isVisible ? 'visible' : ''}`} data-id="element-1119">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden card-hover gradient-border" data-id="element-1120">
              <div className="p-8" data-id="element-1121">
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-1122">
                  Individual
                </h3>
                <p className="text-charcoal/70 mb-6" data-id="element-1123">
                  Perfect for self-paced learning
                </p>
                <div className="flex items-baseline mb-6" data-id="element-1124">
                  <span className="text-4xl font-bold text-charcoal" data-id="element-1125">€19</span>
                  <span className="text-charcoal/60 ml-2" data-id="element-1126">/ course</span>
                </div>
                <ul className="space-y-3 mb-8" data-id="element-1127">
                  <li className="flex items-start" data-id="element-1128">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1129" />
                    <span data-id="element-1130">Instant certification</span>
                  </li>
                  <li className="flex items-start" data-id="element-1131">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1132" />
                    <span data-id="element-1133">Lifetime access to course</span>
                  </li>
                  <li className="flex items-start" data-id="element-1134">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1135" />
                    <span data-id="element-1136">Mobile app access</span>
                  </li>
                  <li className="flex items-start" data-id="element-1137">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1138" />
                    <span data-id="element-1139">Email support</span>
                  </li>
                </ul>
                <a href="/signup/individual" className="block w-full py-3 bg-charcoal text-white font-medium rounded-lg hover:bg-charcoal/90 transition-all duration-300 text-center" data-id="element-1140">
                  Get Started
                </a>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-xl border border-teal/20 overflow-hidden card-hover relative z-10 lg:scale-105" data-id="element-1141">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal to-aqua" data-id="element-1142"></div>
              <div className="p-8" data-id="element-1143">
                <div className="inline-flex items-center px-3 py-1 bg-teal/10 text-teal text-sm font-medium rounded-full mb-2" data-id="element-1144">
                  Most Popular
                </div>
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-1145">
                  All-Access
                </h3>
                <p className="text-charcoal/70 mb-6" data-id="element-1146">
                  Unlimited training for continuous learning
                </p>
                <div className="flex items-baseline mb-6" data-id="element-1147">
                  <span className="text-4xl font-bold text-charcoal" data-id="element-1148">€39</span>
                  <span className="text-charcoal/60 ml-2" data-id="element-1149">/ month</span>
                </div>
                <ul className="space-y-3 mb-8" data-id="element-1150">
                  <li className="flex items-start" data-id="element-1151">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1152" />
                    <span data-id="element-1153">Full access to all 125+ courses</span>
                  </li>
                  <li className="flex items-start" data-id="element-1154">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1155" />
                    <span data-id="element-1156">Unlimited certifications</span>
                  </li>
                  <li className="flex items-start" data-id="element-1157">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1158" />
                    <span data-id="element-1159">New courses as they're released</span>
                  </li>
                  <li className="flex items-start" data-id="element-1160">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1161" />
                    <span data-id="element-1162">Priority support</span>
                  </li>
                </ul>
                <a href="/signup/all-access" className="block w-full py-3 bg-gradient-to-r from-teal to-aqua text-white font-medium rounded-lg hover:opacity-90 transition-all duration-300 text-center shadow-md" data-id="element-1163">
                  Start Free Trial
                </a>
                <p className="text-center text-charcoal/60 text-sm mt-3" data-id="element-1164">
                  30-day free trial, cancel anytime
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden card-hover gradient-border" data-id="element-1165">
              <div className="p-8" data-id="element-1166">
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-1167">Teams</h3>
                <p className="text-charcoal/70 mb-6" data-id="element-1168">
                  Training for your entire organization
                </p>
                <div className="flex items-baseline mb-6" data-id="element-1169">
                  <span className="text-4xl font-bold text-charcoal" data-id="element-1170">€29</span>
                  <span className="text-charcoal/60 ml-2" data-id="element-1171">/ user / month</span>
                </div>
                <ul className="space-y-3 mb-8" data-id="element-1172">
                  <li className="flex items-start" data-id="element-1173">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1174" />
                    <span data-id="element-1175">All All-Access features</span>
                  </li>
                  <li className="flex items-start" data-id="element-1176">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1177" />
                    <span data-id="element-1178">Team management dashboard</span>
                  </li>
                  <li className="flex items-start" data-id="element-1179">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1180" />
                    <span data-id="element-1181">Detailed progress reporting</span>
                  </li>
                  <li className="flex items-start" data-id="element-1182">
                    <CheckIcon className="h-5 w-5 text-teal mr-3 mt-0.5 flex-shrink-0" data-id="element-1183" />
                    <span data-id="element-1184">Volume discounts available</span>
                  </li>
                </ul>
                <a href="/signup/teams" className="block w-full py-3 bg-charcoal text-white font-medium rounded-lg hover:bg-charcoal/90 transition-all duration-300 text-center" data-id="element-1185">
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
          <div className="mt-16 text-center" data-id="element-1186">
            <p className="text-xl text-charcoal/70 mb-6" data-id="element-1187">
              Need a custom solution for your enterprise?
            </p>
            <a href="/enterprise" className="inline-flex items-center px-8 py-4 bg-white text-charcoal font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-all duration-300 shadow-md" data-id="element-1188">
              Learn About Enterprise Solutions
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1189" />
            </a>
          </div>
        </div>
      </div>
    </section>;
};
const CtaSection = () => {
  return <section className="py-20 bg-gradient-to-br from-charcoal to-charcoal/95 relative overflow-hidden" data-id="element-1190">
      <div className="absolute inset-0 overflow-hidden" data-id="element-1191">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-teal/10 blur-3xl" data-id="element-1192"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-aqua/10 blur-3xl" data-id="element-1193"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-1194"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1195">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1196">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-6" data-id="element-1197">
            <ArrowUpRightIcon className="h-8 w-8 text-white" data-id="element-1198" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight" data-id="element-1199">
            Ready to Transform Your <br data-id="element-1200" />
            <span className="gradient-text" data-id="element-1201">Training Experience?</span>
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto" data-id="element-1202">
            Join thousands of organizations that trust Kivarro for their
            compliance and professional development needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center" data-id="element-1203">
            <a href="/explore" className="group relative inline-flex items-center justify-center px-8 py-4 font-medium text-white bg-teal rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-1204">
              <span className="absolute inset-0 w-full h-full bg-gradient-to-br from-teal to-aqua transition-all duration-300 transform group-hover:scale-105" data-id="element-1205"></span>
              <span className="relative flex items-center" data-id="element-1206">
                Start Your Journey
                <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-1207" />
              </span>
            </a>
            <a href="/demo" className="inline-flex items-center justify-center px-8 py-4 font-medium text-white bg-transparent border-2 border-white/30 rounded-lg hover:bg-white/10 transition-all duration-300" data-id="element-1208">
              Book a Demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};